import { useTripsState } from '#states/hooks.js';
import { Button } from '#ui/button.js';
import { MaterialIcon } from '#ui/MaterialIcon.js';

/**
 * The share icon button that opens the share dialog, used in the itinerary hero and the itinerary
 * builder panel header. Shows a green tick badge while the trip's public link is on, and renders
 * nothing when the trip can't be shared (not the owner's, or archived).
 */
export function ShareItineraryButton({
  overImage = false,
  tooltipPosition,
}: {
  /** Over a cover photo: the frosted glass style, which keeps the dark icon readable. */
  overImage?: boolean;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
}) {
  const tripsState = useTripsState();
  if (!tripsState.canShareItinerary) return null;

  const isShared = Boolean(tripsState.shareToken);

  return (
    // The badge sits outside the button: buttons clip their content (`overflow-hidden`), which cut
    // it off at the round edge.
    <span className="relative inline-flex">
      <Button
        variant={overImage ? 'icon-glass' : 'icon'}
        size="icon"
        tooltip={isShared ? 'Shared — anyone with the link can view' : 'Share itinerary'}
        tooltipPosition={tooltipPosition}
        aria-label={isShared ? 'Shared itinerary' : 'Share itinerary'}
        onClick={() => tripsState.openShareDialog()}
      >
        <MaterialIcon icon="ios_share" size="sm" />
      </Button>
      {isShared && (
        // The public link is on: a small tick badge in the lower right corner of the button's square
        // footprint (the round button leaves it empty), so no clipping container can cut it off.
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-green-600 text-white"
        >
          <MaterialIcon icon="check" size={11} weight="bold" />
        </span>
      )}
    </span>
  );
}
